// Terrain-aware placement helpers — pure functions over the shared heightfield so
// procedural objects sit on believable ground (ruins on flats, loot in basins,
// chests on plateaus, nothing floating on a cliff). Deterministic: every draw is
// from the passed rng, every query from terrain.ts. No render/game dependency.

import { terrainSlope, terrainHeight, TerrainConfig, DEFAULT_TERRAIN } from './terrain'

export interface Spot {
  x: number
  z: number
}
interface Rng {
  next(): number
}

const TAU = Math.PI * 2

/**
 * Nudge a desired spot off steep cliffs toward the gentlest nearby ground.
 * Returns the first sampled point at/below maxSlope, else the gentlest seen
 * (best-effort). On flat maps (amp<=0) the input is returned untouched.
 */
export function gentleSpot(rng: Rng, seed: number, amp: number, x: number, z: number, maxSlope: number, attempts = 6, jitter = 4, cfg: TerrainConfig = DEFAULT_TERRAIN): Spot {
  if (amp <= 0) return { x, z }
  let bestX = x
  let bestZ = z
  let bestS = terrainSlope(x, z, seed, amp, cfg)
  if (bestS <= maxSlope) return { x, z }
  for (let i = 0; i < attempts; i++) {
    const a = rng.next() * TAU
    const r = 2 + rng.next() * jitter
    const nx = x + Math.cos(a) * r
    const nz = z + Math.sin(a) * r
    const ns = terrainSlope(nx, nz, seed, amp, cfg)
    if (ns <= maxSlope) return { x: nx, z: nz }
    if (ns < bestS) {
      bestS = ns
      bestX = nx
      bestZ = nz
    }
  }
  return { x: bestX, z: bestZ }
}

/** Bias a spot toward a high, flat plateau (chests / climb-to landmarks). */
export function plateauSpot(rng: Rng, seed: number, amp: number, x: number, z: number, samples = 5, cfg: TerrainConfig = DEFAULT_TERRAIN): Spot {
  if (amp <= 0) return { x, z }
  let best: Spot = { x, z }
  let bestScore = -Infinity
  for (let i = 0; i < samples; i++) {
    const a = rng.next() * TAU
    const r = i === 0 ? 0 : 1 + rng.next() * 4
    const nx = x + Math.cos(a) * r
    const nz = z + Math.sin(a) * r
    const score = terrainHeight(nx, nz, seed, amp, cfg) - terrainSlope(nx, nz, seed, amp, cfg) * 4 // high but flat
    if (score > bestScore) {
      bestScore = score
      best = { x: nx, z: nz }
    }
  }
  return best
}

/**
 * Local piece offsets for a seeded multi-tile structure (composed by the caller from
 * existing cover sprites). Three kinds: a broken wall RING (always leaves a 2-segment
 * gap — never a sealed wall), a platform PAD (4 corners), and a ruin CLUSTER. Pure +
 * deterministic over the rng; the caller maps offsets to world positions + sprites.
 */
export function structureOffsets(rng: Rng): Spot[] {
  const kind = Math.floor(rng.next() * 5)
  const out: Spot[] = []
  if (kind === 0) {
    const rad = 3 + rng.next() * 1.5
    const n = 8
    const gap = Math.floor(rng.next() * n)
    for (let s = 0; s < n; s++) {
      if (s === gap || s === (gap + 1) % n) continue // opening — routable, never sealed
      const a = (s / n) * TAU
      out.push({ x: Math.cos(a) * rad, z: Math.sin(a) * rad })
    }
  } else if (kind === 1) {
    const d = 2.2
    for (const c of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) out.push({ x: c[0] * d, z: c[1] * d })
  } else if (kind === 2) {
    const k = 3 + Math.floor(rng.next() * 3)
    for (let s = 0; s < k; s++) {
      const a = rng.next() * TAU
      const rr = rng.next() * 2.2
      out.push({ x: Math.cos(a) * rr, z: Math.sin(a) * rr })
    }
  } else if (kind === 3) {
    // corridor: two parallel walls (3 pieces each) with both ends open — a walk-through hall
    const halfW = 2.4 + rng.next() * 0.6
    for (const side of [-1, 1]) for (const z of [-2, 0, 2]) out.push({ x: side * halfW, z })
  } else {
    // L-corner: two arms meeting at a corner (5 pieces) — open, never a closed pen
    const step = 1.7 + rng.next() * 0.4
    out.push({ x: 0, z: 0 })
    out.push({ x: step, z: 0 })
    out.push({ x: step * 2, z: 0 })
    out.push({ x: 0, z: step })
    out.push({ x: 0, z: step * 2 })
  }
  return out
}

/**
 * Local offsets for the supporting pillars ringing a central landmark monument (the big
 * centre piece is placed by the caller). One gap is left so the monument is routable, not
 * a sealed wall. Pure + deterministic over the rng.
 */
export function landmarkOffsets(rng: Rng): Spot[] {
  const out: Spot[] = []
  const n = 6
  const rad = 4 + rng.next() * 1.5
  const gap = Math.floor(rng.next() * n)
  for (let s = 0; s < n; s++) {
    if (s === gap) continue // walk-in opening
    const a = (s / n) * TAU + (rng.next() - 0.5) * 0.2
    out.push({ x: Math.cos(a) * rad, z: Math.sin(a) * rad })
  }
  return out
}

/** Bias a spot toward a low basin (loot you explore down into). */
export function basinSpot(rng: Rng, seed: number, amp: number, x: number, z: number, samples = 4, cfg: TerrainConfig = DEFAULT_TERRAIN): Spot {
  if (amp <= 0) return { x, z }
  let best: Spot = { x, z }
  let bestH = Infinity
  for (let i = 0; i < samples; i++) {
    const a = rng.next() * TAU
    const r = i === 0 ? 0 : 1 + rng.next() * 3
    const nx = x + Math.cos(a) * r
    const nz = z + Math.sin(a) * r
    const h = terrainHeight(nx, nz, seed, amp, cfg)
    if (h < bestH) {
      bestH = h
      best = { x: nx, z: nz }
    }
  }
  return best
}
